/**
 * Pure resolver for count-scaled hero ability effects.
 *
 * resolveCountSource maps a HeroCountSource to the non-negative integer count
 * an `attack-per-count` effect scales by. It is pure and total: it reads only
 * `G`, never mutates, never throws, and returns 0 for any source it does not
 * recognize (the union is closed, so this is defensive). No randomness, no
 * clock, no I/O. Counts are resolved from `G` alone — card ext_ids in the
 * zones (victory-bystanders) and the per-card cost in `G.cardStats`
 * (worthy-cards-played-this-turn) — never from registry lookups at runtime.
 *
 * No boardgame.io imports. No .reduce(). No throws.
 */
import { BYSTANDER_EXT_ID } from '../setup/pilesInit.js';
// why: WP-680 / D-24497, corrected D-24529 — the distinct-class source counts the
// colors of Heroes you HAVE (hand + play, self-inclusive, order-independent — the
// rulebook "Heroes you have" term of art), reusing the D-24055 in-play counting for
// the play-area half (incl. getGrantedClasses for Size-Changing) plus the hand.
import { countDistinctHeroClassesYouHave } from './heroConditions.evaluate.js';
// why: WP-680 / D-24391 — the team sources count membership the same way the
// requiresTeam gate does: printed team OR a Copy-Powers-granted team. why (Jeff
// feedback): via cardCountsAsTeamMember, so team 'shield' also folds in the teamless
// basic S.H.I.E.L.D. tokens (Officer/Agent/Trooper) — Legendary Commander must count
// a played Officer as a shield Hero.
import { cardCountsAsTeamMember } from './effectiveTeams.logic.js';
// why: WP-706 / D-24528 — the diagnostics-only explain for the distinct-class source
// mirrors countDistinctHeroClassesYouHave's play-area Set (printed heroClass/heroClass2 plus
// the Size-Changing granted classes), so it imports getGrantedClasses to gather the same
// granted classes. It does NOT call cardHasClassWhenPlayed (the boolean class-match gate).
// why: WP-711 / D-24534 — the per-hero-class-played count sources match a played card's
// class via cardHasClassWhenPlayed (printed hc OR hc2 OR a Size-Changing granted class),
// the class analogue of cardCountsAsTeamMember used by the team sources.
import { cardHasClassWhenPlayed, getGrantedClasses, getCopiedClasses } from './sizeChanging.logic.js';
import { matchesGreyHeroFilter, offPlayCardTraits, playedCardIdsStillInPlay, playedCardIdsThisTurn, } from './splitCard.logic.js';
// why: villain-deck bystanders carry the `bystander-villain-deck-NN` ext_id
// form (villainDeck.setup.ts), distinct from the global-pile `pile-bystander`
// form (BYSTANDER_EXT_ID). The victory pile may hold both, so the count must
// span both ext_id forms.
const VILLAIN_DECK_BYSTANDER_PREFIX = 'bystander-villain-deck-';
// why: WP-673 / D-24488 — a card "makes you Worthy" when it is a Hero costing
// >= 5 (the Worthy definition, D-24464). Mirrors the threshold the parser writes
// into the Worthy condition in setup/heroAbility.setup.ts; kept as a local
// constant per the duplicate-first rule (two uses today, no shared owner).
const WORTHY_HERO_COST_THRESHOLD = 5;
// why: WP-674 / D-24489 — the "costs 4 or more" sibling family counts OTHER
// cards played this turn whose printed cost is >= 4. Local constant per the
// duplicate-first rule (one use, distinct from the Worthy threshold above).
const COST_4_PLUS_THRESHOLD = 4;
// why: WP-824 / D-24757 — Paramilitary Ops counts the OTHER S.H.I.E.L.D. Heroes played
// this turn "that cost 1 or more", so the cost-0 S.H.I.E.L.D. Agent / Trooper starters
// never count while the cost-3 Officer does. Local constant per the duplicate-first rule.
const SHIELD_HERO_MINIMUM_COST = 1;
/**
 * Returns true when an ext_id names a bystander in either ext_id form.
 *
 * @param extId - The card ext_id stored in a zone.
 * @returns Whether the ext_id is a bystander (pile or villain-deck form).
 */
function isBystanderExtId(extId) {
    // why: victory-bystanders counts both bystander ext_id forms
    // (pile-bystander + bystander-villain-deck-NN); villain/henchman/tactic
    // victory-pile cards do not match either form and are excluded.
    return extId === BYSTANDER_EXT_ID || extId.startsWith(VILLAIN_DECK_BYSTANDER_PREFIX);
}
/**
 * Counts the player's victory-pile bystanders across both ext_id forms.
 *
 * @param G - Game state (read-only).
 * @param playerID - The player whose victory pile to count.
 * @returns The number of bystander entries in that player's victory pile.
 */
function countVictoryBystanders(G, playerID) {
    const playerZones = G.playerZones[playerID];
    if (!playerZones) {
        return 0;
    }
    let bystanderCount = 0;
    for (const extId of playerZones.victory) {
        if (isBystanderExtId(extId)) {
            bystanderCount++;
        }
    }
    return bystanderCount;
}
/**
 * Counts the OTHER cards a player has played this turn that make them Worthy.
 *
 * A card "makes you Worthy" when it is a Hero costing >= 5 (D-24464). "Other"
 * excludes the triggering card itself — Divine Lightning's text is "+1 attack
 * for each OTHER card you played this turn that makes you Worthy", and Divine
 * Lightning (cost 5) would otherwise count itself. Cards played this turn live
 * in the in-play zone; a token or basic with no cardStats row (cost 0) never
 * meets the threshold.
 *
 * @param G - Game state (read-only).
 * @param playerID - The player whose in-play zone to count.
 * @param triggeringCardId - The card whose effect is resolving, excluded from the count.
 * @returns The number of other Worthy-making cards played this turn.
 */
function countWorthyCardsPlayedThisTurn(G, playerID, triggeringCardId) {
    const playerZones = G.playerZones[playerID];
    if (!playerZones || !G.cardStats) {
        return 0;
    }
    let worthyCount = 0;
    // why: WP-780 / D-24619 — a rules-facing Hero trait / count read, so a card Penumbra played both-sides counts as both faces.
    for (const playedCardId of playedCardIdsThisTurn(G, playerZones.inPlay)) {
        if (triggeringCardId !== undefined && playedCardId === triggeringCardId) {
            continue;
        }
        // why: safe access — a token in play has no cardStats row (cost 0), so it
        // never meets the >= 5 threshold; only true Heroes contribute.
        const cost = G.cardStats[playedCardId]?.cost ?? 0;
        if (cost >= WORTHY_HERO_COST_THRESHOLD) {
            worthyCount++;
        }
    }
    return worthyCount;
}
/**
 * Counts the OTHER cards a player has played this turn that cost 4 or more.
 *
 * "Other" excludes the triggering card itself — the sibling cards' text is
 * "+N attack/recruit for each OTHER card you played this turn that costs 4 or
 * more", and a triggering card that itself costs >= 4 would otherwise count
 * itself. Cards played this turn live in the in-play zone; a token or basic
 * with no cardStats row (cost 0) never meets the threshold.
 *
 * @param G - Game state (read-only).
 * @param playerID - The player whose in-play zone to count.
 * @param triggeringCardId - The card whose effect is resolving, excluded from the count.
 * @returns The number of other cost-4-plus cards played this turn.
 */
function countCost4PlusCardsPlayedThisTurn(G, playerID, triggeringCardId) {
    const playerZones = G.playerZones[playerID];
    if (!playerZones || !G.cardStats) {
        return 0;
    }
    let cost4PlusCount = 0;
    // why: WP-780 / D-24619 — a rules-facing Hero trait / count read, so a card Penumbra played both-sides counts as both faces.
    for (const playedCardId of playedCardIdsThisTurn(G, playerZones.inPlay)) {
        if (triggeringCardId !== undefined && playedCardId === triggeringCardId) {
            continue;
        }
        // why: safe access — a token in play has no cardStats row (cost 0), so it
        // never meets the >= 4 threshold; only true Heroes contribute.
        const cost = G.cardStats[playedCardId]?.cost ?? 0;
        if (cost >= COST_4_PLUS_THRESHOLD) {
            cost4PlusCount++;
        }
    }
    return cost4PlusCount;
}
/**
 * Counts the OTHER cards a player has played this turn that show a given power icon.
 *
 * "Show an attack/recruit icon" is the FAITHFUL presence test — `hasAttackIcon` /
 * `hasRecruitIcon` on `G.cardStats`, set at setup from the RAW registry value being
 * non-null (WP-675 / D-24490). A `"0+"` card shows the icon but parses to cost/attack 0,
 * so this must NOT test `attack > 0`. "Other" excludes the triggering card itself —
 * vnom's text is "for each OTHER card you played this turn with a … icon". Cards played
 * this turn live in the in-play zone; a card with no cardStats row never counts.
 *
 * @param G - Game state (read-only).
 * @param playerID - The player whose in-play zone to count.
 * @param triggeringCardId - The card whose effect is resolving, excluded from the count.
 * @param icon - Which printed icon to count: 'attack' or 'recruit'.
 * @returns The number of other cards played this turn that show the given icon.
 */
function countIconCardsPlayedThisTurn(G, playerID, triggeringCardId, icon) {
    const playerZones = G.playerZones[playerID];
    if (!playerZones || !G.cardStats) {
        return 0;
    }
    let iconCount = 0;
    // why: WP-780 / D-24619 — a rules-facing Hero trait / count read, so a card Penumbra played both-sides counts as both faces.
    for (const playedCardId of playedCardIdsThisTurn(G, playerZones.inPlay)) {
        if (triggeringCardId !== undefined && playedCardId === triggeringCardId) {
            continue;
        }
        // why: faithful icon presence from the setup-derived boolean, not a >0 proxy;
        // a card with no cardStats row (missing entry) never shows the icon.
        const stat = G.cardStats[playedCardId];
        const showsIcon = icon === 'attack' ? stat?.hasAttackIcon === true : stat?.hasRecruitIcon === true;
        if (showsIcon) {
            iconCount++;
        }
    }
    return iconCount;
}
/**
 * Counts the player's S.H.I.E.L.D. Level: the S.H.I.E.L.D./HYDRA cards in their
 * Victory Pile (WP-677 / D-24493).
 *
 * why: per universal-rules-v23 §S.H.I.E.L.D. Level, your Level is "the number of
 * S.H.I.E.L.D. and/or HYDRA cards in your Victory Pile" — counted via the
 * setup-derived `isShieldOrHydra` flag (team icon OR name/group/mastermind substring).
 * Unlike the "each OTHER card" sources this takes NO `triggeringCardId` and does NOT
 * self-exclude — it counts the WHOLE pile and "never consumes the cards — it just
 * checks." A victory-pile card with no cardStats row does not qualify.
 *
 * @param G - Game state (read-only).
 * @param playerID - The player whose Victory Pile to count.
 * @returns The number of S.H.I.E.L.D./HYDRA cards in that player's Victory Pile.
 */
function countShieldLevels(G, playerID) {
    const playerZones = G.playerZones[playerID];
    if (!playerZones || !G.cardStats) {
        return 0;
    }
    let shieldCount = 0;
    for (const extId of playerZones.victory) {
        if (G.cardStats[extId]?.isShieldOrHydra === true) {
            shieldCount++;
        }
    }
    return shieldCount;
}
/**
 * Counts the OTHER cards a player has played this turn on a given team.
 *
 * "Other" excludes the triggering card itself — the cards' text is "for each
 * OTHER [team] you played this turn" (Captain America's A Day Unlike Any Other,
 * Nick Fury's Legendary Commander). Membership uses `cardHasTeamWhenPlayed`, so
 * a card counts by its printed team OR a Copy-Powers-granted team (D-24391),
 * exactly as the `requiresTeam` gate reads it. A card with no team never counts.
 *
 * @param G - Game state (read-only).
 * @param playerID - The player whose in-play zone to count.
 * @param triggeringCardId - The card whose effect is resolving, excluded from the count.
 * @param team - The team slug to match (e.g. 'avengers', 'shield').
 * @returns The number of other cards played this turn on that team.
 */
function countTeamCardsPlayedThisTurn(G, playerID, triggeringCardId, team) {
    const playerZones = G.playerZones[playerID];
    if (!playerZones) {
        return 0;
    }
    let teamCount = 0;
    // why: WP-780 / D-24619 — a rules-facing Hero trait / count read, so a card Penumbra played both-sides counts as both faces.
    for (const playedCardId of playedCardIdsThisTurn(G, playerZones.inPlay)) {
        if (triggeringCardId !== undefined && playedCardId === triggeringCardId) {
            continue;
        }
        if (cardCountsAsTeamMember(G, playedCardId, team)) {
            teamCount++;
        }
    }
    return teamCount;
}
/**
 * Counts the OTHER S.H.I.E.L.D. Heroes a player has played this turn that cost 1 or more.
 *
 * The cost-filtered sibling of countTeamCardsPlayedThisTurn(…, 'shield') (WP-824 /
 * D-24757). "Other" excludes the triggering card — Paramilitary Ops' text is "+1 attack
 * for each OTHER [team:shield] Hero you played this turn that costs 1 or more".
 * Membership uses cardCountsAsTeamMember, so a basic S.H.I.E.L.D. Officer (cost 3 in
 * G.cardStats) counts, while the S.H.I.E.L.D. Agent / Trooper starters (cardStats rows with
 * cost 0, their printed cost) never do. A card with no cardStats row reads cost 0 (`?? 0`).
 *
 * @param G - Game state (read-only).
 * @param playerID - The player whose in-play zone to count.
 * @param triggeringCardId - The card whose effect is resolving, excluded from the count.
 * @returns The number of other S.H.I.E.L.D. Heroes costing 1 or more played this turn.
 */
function countShieldHeroesCostOnePlusPlayedThisTurn(G, playerID, triggeringCardId) {
    const playerZones = G.playerZones[playerID];
    if (!playerZones) {
        return 0;
    }
    let shieldHeroCount = 0;
    // why: WP-780 / D-24619 — a rules-facing Hero trait / count read, so a card Penumbra played both-sides counts as both faces.
    for (const playedCardId of playedCardIdsThisTurn(G, playerZones.inPlay)) {
        if (triggeringCardId !== undefined && playedCardId === triggeringCardId) {
            continue;
        }
        if (!cardCountsAsTeamMember(G, playedCardId, 'shield')) {
            continue;
        }
        const cost = G.cardStats?.[playedCardId]?.cost ?? 0;
        if (cost >= SHIELD_HERO_MINIMUM_COST) {
            shieldHeroCount++;
        }
    }
    return shieldHeroCount;
}
/**
 * Counts the OTHER cards a player has played this turn whose hero class matches.
 *
 * The class analogue of countTeamCardsPlayedThisTurn (WP-711 / D-24534). "Other"
 * excludes the triggering card itself — each card's text is "+N for each OTHER
 * [class] Hero you played this turn" (Marvelous Strength, Absorb Energies, the
 * dkcy/bkwd/co2e lines). Membership uses cardHasClassWhenPlayed, so a card counts
 * by its printed heroClass, its heroClass2 (D-24523 dual-class), OR a Size-Changing
 * granted class (D-24074) — exactly as the heroClassMatch gate reads it. A card
 * with no matching class never counts.
 *
 * @param G - Game state (read-only).
 * @param playerID - The player whose in-play zone to count.
 * @param triggeringCardId - The card whose effect is resolving, excluded from the count.
 * @param heroClass - The hero-class slug to match (e.g. 'strength', 'ranged').
 * @returns The number of other cards played this turn of that hero class.
 */
function countHeroClassCardsPlayedThisTurn(G, playerID, triggeringCardId, heroClass) {
    const playerZones = G.playerZones[playerID];
    if (!playerZones) {
        return 0;
    }
    let classCount = 0;
    // why: WP-780 / D-24619 — a rules-facing Hero trait / count read, so a card Penumbra played both-sides counts as both faces.
    for (const playedCardId of playedCardIdsThisTurn(G, playerZones.inPlay)) {
        // why: "each OTHER [class] Hero you played this turn" — exclude the triggering card itself.
        if (triggeringCardId !== undefined && playedCardId === triggeringCardId) {
            continue;
        }
        if (cardHasClassWhenPlayed(G, playedCardId, heroClass)) {
            classCount++;
        }
    }
    return classCount;
}
/**
 * Counts the OTHER cards a player has played this turn whose printed cost is odd.
 *
 * "Other" excludes the triggering card itself — Deadpool's Oddball reads "+1 attack
 * for each OTHER Hero with an odd-numbered cost you played this turn". The generated
 * card text prints an `[icon:vp]`, but hero cards carry no victory-point value in the
 * data model, so this scales by odd COST (Jeff-confirmed 2026-09-09, D-24497). A card
 * with no `cardStats` row is cost 0 (even) and never counts.
 *
 * @param G - Game state (read-only).
 * @param playerID - The player whose in-play zone to count.
 * @param triggeringCardId - The card whose effect is resolving, excluded from the count.
 * @returns The number of other cards played this turn with an odd printed cost.
 */
function countOddCostCardsPlayedThisTurn(G, playerID, triggeringCardId) {
    const playerZones = G.playerZones[playerID];
    if (!playerZones || !G.cardStats) {
        return 0;
    }
    let oddCostCount = 0;
    // why: WP-780 / D-24619 — a rules-facing Hero trait / count read, so a card Penumbra played both-sides counts as both faces.
    for (const playedCardId of playedCardIdsThisTurn(G, playerZones.inPlay)) {
        if (triggeringCardId !== undefined && playedCardId === triggeringCardId) {
            continue;
        }
        const cost = G.cardStats[playedCardId]?.cost ?? 0;
        if (cost % 2 === 1) {
            oddCostCount++;
        }
    }
    return oddCostCount;
}
/**
 * Resolves a count source to the non-negative integer it represents.
 *
 * Pure and total: reads only `G`, never mutates or throws, and returns 0 for
 * any unrecognized source. The returned value is the count an `attack-per-count`
 * effect multiplies by its per-unit magnitude.
 *
 * @param G - Game state (read-only).
 * @param playerID - The active player whose state to read.
 * @param source - The count source to resolve.
 * @param triggeringCardId - The card whose effect is resolving, if any. Used by
 *   sources that must exclude the triggering card ("each OTHER card …");
 *   sources that read a zone the triggering card is never in (victory-bystanders)
 *   ignore it.
 * @returns A non-negative integer count (0 for an unknown source).
 */
export function resolveCountSource(G, playerID, source, triggeringCardId) {
    switch (source) {
        case 'victory-bystanders': {
            return countVictoryBystanders(G, playerID);
        }
        case 'worthy-cards-played-this-turn': {
            return countWorthyCardsPlayedThisTurn(G, playerID, triggeringCardId);
        }
        case 'cost-four-plus-played-this-turn': {
            return countCost4PlusCardsPlayedThisTurn(G, playerID, triggeringCardId);
        }
        case 'attack-icon-played-this-turn': {
            return countIconCardsPlayedThisTurn(G, playerID, triggeringCardId, 'attack');
        }
        case 'recruit-icon-played-this-turn': {
            return countIconCardsPlayedThisTurn(G, playerID, triggeringCardId, 'recruit');
        }
        case 'shield-levels': {
            // why: WP-677 / D-24493 — counts the whole Victory Pile (no self-exclusion);
            // triggeringCardId is intentionally ignored (S.H.I.E.L.D. Level "just checks").
            return countShieldLevels(G, playerID);
        }
        case 'distinct-hero-classes-played-this-turn': {
            // why: WP-680 / D-24497, semantics corrected D-24529 — "for each color of Hero
            // you have" counts the distinct colors among the Heroes you HAVE (your HAND +
            // play area), SELF-INCLUSIVE and ORDER-INDEPENDENT, so a Tech card still in hand
            // counts even when this card is played first. triggeringCardId is intentionally
            // ignored. (The slug keeps the "played-this-turn" wording as a stable id.)
            return countDistinctHeroClassesYouHave(G, playerID);
        }
        case 'avengers-played-this-turn': {
            return countTeamCardsPlayedThisTurn(G, playerID, triggeringCardId, 'avengers');
        }
        case 'shield-heroes-played-this-turn': {
            return countTeamCardsPlayedThisTurn(G, playerID, triggeringCardId, 'shield');
        }
        case 'shield-heroes-cost-one-plus-played-this-turn': {
            // why: WP-824 / D-24757 — Paramilitary Ops' "each other S.H.I.E.L.D. Hero you
            // played this turn that costs 1 or more" (starters cost 0 and never count).
            return countShieldHeroesCostOnePlusPlayedThisTurn(G, playerID, triggeringCardId);
        }
        case 'x-men-played-this-turn': {
            // why: D-24655 — X-Men United's "each other X-Men Hero you played this turn".
            return countTeamCardsPlayedThisTurn(G, playerID, triggeringCardId, 'x-men');
        }
        case 'odd-cost-heroes-played-this-turn': {
            return countOddCostCardsPlayedThisTurn(G, playerID, triggeringCardId);
        }
        // why: WP-711 / D-24534 — the per-hero-class-played family; each counts OTHER
        // inPlay cards of that class (cardHasClassWhenPlayed), self-exclusive.
        case 'strength-heroes-played-this-turn': {
            return countHeroClassCardsPlayedThisTurn(G, playerID, triggeringCardId, 'strength');
        }
        case 'ranged-heroes-played-this-turn': {
            return countHeroClassCardsPlayedThisTurn(G, playerID, triggeringCardId, 'ranged');
        }
        case 'tech-heroes-played-this-turn': {
            return countHeroClassCardsPlayedThisTurn(G, playerID, triggeringCardId, 'tech');
        }
        case 'covert-heroes-played-this-turn': {
            return countHeroClassCardsPlayedThisTurn(G, playerID, triggeringCardId, 'covert');
        }
        case 'extra-cards-drawn-this-turn': {
            // why: D-24732 — "for each extra card you've drawn this turn" counts every card the
            // current player drew this turn other than the hand drawn at end of turn. Every
            // effect-draw site for the current player increments turnEconomy.cardsDrawn and the
            // start-of-turn fill never does, so the counter IS the count. Self-inclusive: a
            // card's own earlier draw line has already landed when its count line resolves.
            // playerID / triggeringCardId are unused because turnEconomy is the current
            // player's alone; the ?? 0 guards narrow test mocks that omit the field.
            return G.turnEconomy?.cardsDrawn ?? 0;
        }
        case 'non-grey-heroes-koed-from-deck-this-turn': {
            // why: WP-859 / D-24795 — World-Spanning Hunger's "+2 for each non-grey Hero that was KO'd
            // from your deck this turn": the non-grey Heroes in this player's deck-KO turn record.
            // Wounds, Bystanders and grey Heroes fail the filter. triggeringCardId is unused (the record
            // holds KO'd cards, never the resolving card). An absent record counts 0.
            let nonGreyHeroCount = 0;
            for (const koedCardId of G.cardsKoedFromDeckThisTurn?.[playerID] ?? []) {
                if (matchesGreyHeroFilter(G, koedCardId, 'non-grey')) {
                    nonGreyHeroCount += 1;
                }
            }
            return nonGreyHeroCount;
        }
        default: {
            // why: defensive — the union is closed, but an unrecognized source must
            // resolve to 0 (a skipped no-op grant) rather than throw.
            return 0;
        }
    }
}
// ---------------------------------------------------------------------------
// explainCountSourceInputs — diagnostics-only counted-inputs explain (WP-706 / D-24528)
// ---------------------------------------------------------------------------
/**
 * Collects the OTHER played-this-turn cards whose printed cost meets a threshold.
 *
 * Mirrors countWorthyCardsPlayedThisTurn / countCost4PlusCardsPlayedThisTurn in
 * ext-id-collecting form: self-EXCLUSIVE (skips the triggering card), so the
 * returned list length equals the counter's integer.
 *
 * @param G - Game state (read-only).
 * @param playerID - The player whose in-play zone to scan.
 * @param triggeringCardId - The resolving card, excluded from the list.
 * @param threshold - The minimum printed cost a card must meet to be collected.
 * @returns The ext_ids of the other cards played this turn meeting the threshold.
 */
function collectCostThresholdCardsPlayedThisTurn(G, playerID, triggeringCardId, threshold) {
    const playerZones = G.playerZones[playerID];
    if (!playerZones || !G.cardStats) {
        return [];
    }
    const matchedCardIds = [];
    // why: WP-780 / D-24619 — a rules-facing Hero trait / count read, so a card Penumbra played both-sides counts as both faces.
    for (const playedCardId of playedCardIdsThisTurn(G, playerZones.inPlay)) {
        if (triggeringCardId !== undefined && playedCardId === triggeringCardId) {
            continue;
        }
        // why: safe access — a token in play has no cardStats row (cost 0), so it never
        // meets the threshold; mirrors the counter's `?? 0` guard exactly.
        const cost = G.cardStats[playedCardId]?.cost ?? 0;
        if (cost >= threshold) {
            matchedCardIds.push(playedCardId);
        }
    }
    return matchedCardIds;
}
/**
 * Collects the OTHER played-this-turn cards that show a given power icon.
 *
 * Mirrors countIconCardsPlayedThisTurn in ext-id-collecting form: the FAITHFUL
 * presence test on the setup-derived `hasAttackIcon` / `hasRecruitIcon` boolean
 * (WP-675 / D-24490), NOT a `> 0` proxy. Self-EXCLUSIVE (skips the triggering card).
 *
 * @param G - Game state (read-only).
 * @param playerID - The player whose in-play zone to scan.
 * @param triggeringCardId - The resolving card, excluded from the list.
 * @param icon - Which printed icon to collect: 'attack' or 'recruit'.
 * @returns The ext_ids of the other cards played this turn showing the given icon.
 */
function collectIconCardsPlayedThisTurn(G, playerID, triggeringCardId, icon) {
    const playerZones = G.playerZones[playerID];
    if (!playerZones || !G.cardStats) {
        return [];
    }
    const matchedCardIds = [];
    // why: WP-780 / D-24619 — a rules-facing Hero trait / count read, so a card Penumbra played both-sides counts as both faces.
    for (const playedCardId of playedCardIdsThisTurn(G, playerZones.inPlay)) {
        if (triggeringCardId !== undefined && playedCardId === triggeringCardId) {
            continue;
        }
        const stat = G.cardStats[playedCardId];
        const showsIcon = icon === 'attack' ? stat?.hasAttackIcon === true : stat?.hasRecruitIcon === true;
        if (showsIcon) {
            matchedCardIds.push(playedCardId);
        }
    }
    return matchedCardIds;
}
/**
 * Collects the OTHER played-this-turn cards on a given team.
 *
 * Mirrors countTeamCardsPlayedThisTurn in ext-id-collecting form, reusing the SAME
 * `cardCountsAsTeamMember` predicate (printed team OR a Copy-Powers-granted team,
 * D-24391; folds in the teamless basic S.H.I.E.L.D. tokens for team 'shield').
 * Self-EXCLUSIVE (skips the triggering card).
 *
 * @param G - Game state (read-only).
 * @param playerID - The player whose in-play zone to scan.
 * @param triggeringCardId - The resolving card, excluded from the list.
 * @param team - The team slug to match (e.g. 'avengers', 'shield').
 * @returns The ext_ids of the other cards played this turn on that team.
 */
function collectTeamCardsPlayedThisTurn(G, playerID, triggeringCardId, team) {
    const playerZones = G.playerZones[playerID];
    if (!playerZones) {
        return [];
    }
    const matchedCardIds = [];
    // why: WP-780 / D-24619 — a rules-facing Hero trait / count read, so a card Penumbra played both-sides counts as both faces.
    for (const playedCardId of playedCardIdsThisTurn(G, playerZones.inPlay)) {
        if (triggeringCardId !== undefined && playedCardId === triggeringCardId) {
            continue;
        }
        if (cardCountsAsTeamMember(G, playedCardId, team)) {
            matchedCardIds.push(playedCardId);
        }
    }
    return matchedCardIds;
}
/**
 * Collects the OTHER played-this-turn S.H.I.E.L.D. Heroes that cost 1 or more.
 *
 * Mirrors countShieldHeroesCostOnePlusPlayedThisTurn in ext-id-collecting form
 * (WP-824 / D-24757): self-EXCLUSIVE, so the returned list length equals the counter's
 * integer.
 *
 * @param G - Game state (read-only).
 * @param playerID - The player whose in-play zone to scan.
 * @param triggeringCardId - The resolving card, excluded from the list.
 * @returns The ext_ids of the other S.H.I.E.L.D. Heroes costing 1 or more played this turn.
 */
function collectShieldHeroesCostOnePlusPlayedThisTurn(G, playerID, triggeringCardId) {
    const playerZones = G.playerZones[playerID];
    if (!playerZones) {
        return [];
    }
    const matchedCardIds = [];
    // why: WP-780 / D-24619 — a rules-facing Hero trait / count read, so a card Penumbra played both-sides counts as both faces.
    for (const playedCardId of playedCardIdsThisTurn(G, playerZones.inPlay)) {
        if (triggeringCardId !== undefined && playedCardId === triggeringCardId) {
            continue;
        }
        if (!cardCountsAsTeamMember(G, playedCardId, 'shield')) {
            continue;
        }
        // why: mirrors the counter's cost read exactly (a starter's cardStats cost is 0; `?? 0` for no row).
        const cost = G.cardStats?.[playedCardId]?.cost ?? 0;
        if (cost >= SHIELD_HERO_MINIMUM_COST) {
            matchedCardIds.push(playedCardId);
        }
    }
    return matchedCardIds;
}
/**
 * Collects the OTHER played-this-turn cards whose printed cost is odd.
 *
 * Mirrors countOddCostCardsPlayedThisTurn in ext-id-collecting form. Self-EXCLUSIVE
 * (skips the triggering card); a card with no `cardStats` row is cost 0 (even).
 *
 * @param G - Game state (read-only).
 * @param playerID - The player whose in-play zone to scan.
 * @param triggeringCardId - The resolving card, excluded from the list.
 * @returns The ext_ids of the other cards played this turn with an odd printed cost.
 */
function collectOddCostCardsPlayedThisTurn(G, playerID, triggeringCardId) {
    const playerZones = G.playerZones[playerID];
    if (!playerZones || !G.cardStats) {
        return [];
    }
    const matchedCardIds = [];
    // why: WP-780 / D-24619 — a rules-facing Hero trait / count read, so a card Penumbra played both-sides counts as both faces.
    for (const playedCardId of playedCardIdsThisTurn(G, playerZones.inPlay)) {
        if (triggeringCardId !== undefined && playedCardId === triggeringCardId) {
            continue;
        }
        const cost = G.cardStats[playedCardId]?.cost ?? 0;
        if (cost % 2 === 1) {
            matchedCardIds.push(playedCardId);
        }
    }
    return matchedCardIds;
}
/**
 * Collects the OTHER played-this-turn cards of a given hero class (WP-711 / D-24534).
 *
 * Mirrors countHeroClassCardsPlayedThisTurn in ext-id-collecting form, reusing the SAME
 * cardHasClassWhenPlayed predicate (printed hc OR hc2 OR a Size-Changing granted class).
 * Self-EXCLUSIVE (skips the triggering card), so count === length.
 *
 * @param G - Game state (read-only).
 * @param playerID - The player whose in-play zone to scan.
 * @param triggeringCardId - The resolving card, excluded from the list.
 * @param heroClass - The hero-class slug to match.
 * @returns The ext_ids of the other cards played this turn of that hero class.
 */
function collectHeroClassCardsPlayedThisTurn(G, playerID, triggeringCardId, heroClass) {
    const playerZones = G.playerZones[playerID];
    if (!playerZones) {
        return [];
    }
    const matchedCardIds = [];
    // why: WP-780 / D-24619 — a rules-facing Hero trait / count read, so a card Penumbra played both-sides counts as both faces.
    for (const playedCardId of playedCardIdsThisTurn(G, playerZones.inPlay)) {
        if (triggeringCardId !== undefined && playedCardId === triggeringCardId) {
            continue;
        }
        if (cardHasClassWhenPlayed(G, playedCardId, heroClass)) {
            matchedCardIds.push(playedCardId);
        }
    }
    return matchedCardIds;
}
/**
 * Collects the cards you HAVE (hand + play) that contribute at least one distinct hero class.
 *
 * Mirrors countDistinctHeroClassesYouHave's gathering in ext-id-collecting form: the HAND
 * half by printed `heroClass` / `heroClass2` only, and the PLAY half by those plus the
 * Size-Changing granted classes (an in-play-only grant). It is SELF-INCLUSIVE — the count
 * source counts distinct COLOURS over all Heroes you have including the trigger, so this
 * takes no `triggeringCardId` and lists every card that carries any class. The resolved
 * count is a distinct-colour rollup, so it is NOT bounded by `collected.length` in either
 * direction: multiple cards may share a colour (count below length), and ONE card may carry
 * two colours — a dual-class card, or a split card in hand (both halves, WP-772 / D-24604) —
 * pushing count above length. Neither is an error.
 *
 * // why: D-24529 — must scan hand + play (not just inPlay) so the countedInputs stay
 * consistent with the corrected resolveCountSource count; a hand-only colour would
 * otherwise push count above the returned length and break the WP-706 invariant.
 *
 * @param G - Game state (read-only).
 * @param playerID - The player whose hand + in-play zones to scan.
 * @returns The ext_ids of the Heroes-you-have contributing any hero class.
 */
function collectDistinctHeroClassCards(G, playerID) {
    const playerZones = G.playerZones[playerID];
    if (!playerZones || !G.cardTraits) {
        return [];
    }
    const matchedCardIds = [];
    // why: HAND half — printed colours only (no Size-Changing grant, an in-play effect).
    for (const handCardId of playerZones.hand) {
        // why: WP-772 / D-24604 — consistency with countDistinctHeroClassesYouHave: a split card in
        // hand reads the off-play view (both halves' classes) through the split-face map. Face a
        // always carries a class, so this does not change which hand cards are collected.
        const traitEntry = offPlayCardTraits(G, handCardId);
        const hasPrintedClass = traitEntry !== undefined && typeof traitEntry.heroClass === 'string' && traitEntry.heroClass.length > 0;
        // why: WP-703 / D-24523 — a dual-class card contributes via heroClass2 alone.
        const hasSecondClass = traitEntry !== undefined && typeof traitEntry.heroClass2 === 'string' && traitEntry.heroClass2.length > 0;
        if (hasPrintedClass || hasSecondClass) {
            matchedCardIds.push(handCardId);
        }
    }
    // why: PLAY half — printed colours plus Size-Changing granted classes.
    // why: WP-780 / D-24619 — a rules-facing Hero trait / count read, so a card Penumbra played both-sides counts as both faces.
    // why: WP-803 / D-24692 — a "Heroes you have" read (hand + still in play), so a card that went Out of Time is excluded: rules v23 L3443-3447 (a played card that moved elsewhere doesn't count as "Your Heroes" or "Heroes you Have") + L3457-3470 ("Reveal a Hero" / "KO one of your Heroes" can't pick it).
    for (const playedCardId of playedCardIdsStillInPlay(G, playerZones.inPlay)) {
        const traitEntry = G.cardTraits[playedCardId];
        const hasPrintedClass = traitEntry !== undefined && typeof traitEntry.heroClass === 'string' && traitEntry.heroClass.length > 0;
        // why: WP-703 / D-24523 — a dual-class card contributes via heroClass2 alone; this
        // is the live-observability tie (a card whose colour is matched ONLY via heroClass2
        // must still appear in the counted inputs).
        const hasSecondClass = traitEntry !== undefined && typeof traitEntry.heroClass2 === 'string' && traitEntry.heroClass2.length > 0;
        // why: D-24074 — a Size-Changing card contributes each granted class too.
        // why: D-24730 — and Copy Powers contributes the class it copied this play.
        const hasGrantedClass = getGrantedClasses(G, playedCardId).length > 0 ||
            getCopiedClasses(G, playedCardId).length > 0;
        if (hasPrintedClass || hasSecondClass || hasGrantedClass) {
            matchedCardIds.push(playedCardId);
        }
    }
    // why: WP-858 / D-24794 — CONTROLLED-ARTIFACT half, mirroring countDistinctHeroClassesYouHave
    // (printed classes only), so the counted inputs stay consistent with the count.
    for (const artifactCardId of G.controlledArtifacts?.[playerID] ?? []) {
        const traitEntry = G.cardTraits[artifactCardId];
        const hasPrintedClass = traitEntry !== undefined && typeof traitEntry.heroClass === 'string' && traitEntry.heroClass.length > 0;
        const hasSecondClass = traitEntry !== undefined && typeof traitEntry.heroClass2 === 'string' && traitEntry.heroClass2.length > 0;
        if (hasPrintedClass || hasSecondClass) {
            matchedCardIds.push(artifactCardId);
        }
    }
    return matchedCardIds;
}
/**
 * Diagnostics-only explain: the ext_ids a count source counted (WP-706 / D-24528).
 *
 * why: this is a SEPARATE read-only pass, NOT folded into `resolveCountSource`, so the
 * gameplay grant integer stays byte-identical (determinism — the resolver is untouched).
 * It mirrors each played-this-turn counter's matching logic in ext-id-collecting form
 * (sharing `cardCountsAsTeamMember`; mirroring `countDistinctHeroClassesYouHave`'s play-area
 * class Set — it does NOT call `cardHasClassWhenPlayed`). It returns `[]` for the
 * victory-pile sources (`victory-bystanders`, `shield-levels`) — those enumerate the
 * Victory Pile, not cards-played-this-turn, and are omitted from `countedInputs` in this
 * slice. Self-inclusion follows each source: self-EXCLUSIVE for the seven per-card sources
 * (`count === length`), self-INCLUSIVE for `distinct-hero-classes-played-this-turn`
 * (a distinct-colour rollup — count may be below or above length; see
 * collectDistinctHeroClassCards). Pure, total, never mutates `G`, no I/O.
 *
 * @param G - Game state (read-only).
 * @param playerID - The active player whose state to read.
 * @param source - The count source to explain.
 * @param triggeringCardId - The resolving card, if any (self-exclusion for the per-card sources).
 * @returns The counted cards' ext_ids, or `[]` for the victory-pile / unknown sources.
 */
export function explainCountSourceInputs(G, playerID, source, triggeringCardId) {
    switch (source) {
        case 'victory-bystanders': {
            // why: victory-pile source — enumerates the Victory Pile, not cards played this
            // turn; countedInputs is omitted for it in this slice (returns []).
            return [];
        }
        case 'shield-levels': {
            // why: victory-pile source — same as victory-bystanders; omitted this slice.
            return [];
        }
        case 'worthy-cards-played-this-turn': {
            return collectCostThresholdCardsPlayedThisTurn(G, playerID, triggeringCardId, WORTHY_HERO_COST_THRESHOLD);
        }
        case 'cost-four-plus-played-this-turn': {
            return collectCostThresholdCardsPlayedThisTurn(G, playerID, triggeringCardId, COST_4_PLUS_THRESHOLD);
        }
        case 'attack-icon-played-this-turn': {
            return collectIconCardsPlayedThisTurn(G, playerID, triggeringCardId, 'attack');
        }
        case 'recruit-icon-played-this-turn': {
            return collectIconCardsPlayedThisTurn(G, playerID, triggeringCardId, 'recruit');
        }
        case 'distinct-hero-classes-played-this-turn': {
            // why: self-INCLUSIVE — mirrors resolveCountSource's own self-inclusion for this
            // source (no triggeringCardId exclusion); the resolved count is a distinct-colour
            // rollup, not bounded by the returned card-list length (one dual-class or hand split
            // card can contribute two colours).
            return collectDistinctHeroClassCards(G, playerID);
        }
        case 'avengers-played-this-turn': {
            return collectTeamCardsPlayedThisTurn(G, playerID, triggeringCardId, 'avengers');
        }
        case 'shield-heroes-played-this-turn': {
            return collectTeamCardsPlayedThisTurn(G, playerID, triggeringCardId, 'shield');
        }
        case 'shield-heroes-cost-one-plus-played-this-turn': {
            return collectShieldHeroesCostOnePlusPlayedThisTurn(G, playerID, triggeringCardId);
        }
        case 'x-men-played-this-turn': {
            return collectTeamCardsPlayedThisTurn(G, playerID, triggeringCardId, 'x-men');
        }
        case 'odd-cost-heroes-played-this-turn': {
            return collectOddCostCardsPlayedThisTurn(G, playerID, triggeringCardId);
        }
        // why: WP-711 / D-24534 — self-EXCLUSIVE per-hero-class collectors mirroring the
        // resolveCountSource branches (count === length).
        case 'strength-heroes-played-this-turn': {
            return collectHeroClassCardsPlayedThisTurn(G, playerID, triggeringCardId, 'strength');
        }
        case 'ranged-heroes-played-this-turn': {
            return collectHeroClassCardsPlayedThisTurn(G, playerID, triggeringCardId, 'ranged');
        }
        case 'tech-heroes-played-this-turn': {
            return collectHeroClassCardsPlayedThisTurn(G, playerID, triggeringCardId, 'tech');
        }
        case 'covert-heroes-played-this-turn': {
            return collectHeroClassCardsPlayedThisTurn(G, playerID, triggeringCardId, 'covert');
        }
        case 'extra-cards-drawn-this-turn': {
            // why: D-24732 — a counter source, not a card list: turnEconomy.cardsDrawn records
            // how many cards were drawn, not which, so there are no inputs to name (like the
            // victory-pile sources, countedInputs is omitted for it).
            return [];
        }
        case 'non-grey-heroes-koed-from-deck-this-turn': {
            // why: WP-859 / D-24795 — the ids resolveCountSource counts, in KO order (count === length).
            const nonGreyHeroIds = [];
            for (const koedCardId of G.cardsKoedFromDeckThisTurn?.[playerID] ?? []) {
                if (matchesGreyHeroFilter(G, koedCardId, 'non-grey')) {
                    nonGreyHeroIds.push(koedCardId);
                }
            }
            return nonGreyHeroIds;
        }
        default: {
            // why: defensive — the union is closed, but an unrecognized source explains to no
            // inputs (mirrors resolveCountSource's defensive 0) rather than throwing.
            return [];
        }
    }
}
//# sourceMappingURL=heroCountSource.resolve.js.map